import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { theme } from '@/lib/theme';
import type { AvailabilityRow, BreakInterval } from '@/types';

const DAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const DAY_LABELS_FULL = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const NIGHT_START = 19 * 60; // 19:00 in minutes

const DEFAULT_ROW = (day: number): AvailabilityRow => ({
  id: '',
  barber_id: '',
  day_of_week: day,
  is_working: day >= 1 && day <= 6,
  start_time: '08:30',
  end_time: '18:00',
  break_start: '12:00',
  break_end: '13:30',
  breaks: [{ start: '12:00', end: '13:30' }],
  created_at: '',
  updated_at: '',
});

function fmt(t: string | null | undefined): string {
  if (!t) return '';
  return t.slice(0, 5);
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function isNightTime(time: string): boolean {
  return timeToMinutes(time) >= NIGHT_START;
}

// ─── Break Item ───────────────────────────────────────────────────────────────

interface BreakItemProps {
  brk: BreakInterval;
  index: number;
  onChange: (index: number, field: 'start' | 'end', value: string) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

function BreakItem({ brk, index, onChange, onRemove, canRemove }: BreakItemProps) {
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        {index === 0 && (
          <label className="text-xs mb-1 block" style={{ color: theme.colors.textDisabled }}>Inicio</label>
        )}
        <input
          type="time"
          value={brk.start}
          onChange={(e) => onChange(index, 'start', e.target.value)}
          className="w-full rounded-lg px-3 py-2 text-sm text-white border outline-none"
          style={{ backgroundColor: theme.colors.bgSecondary, borderColor: theme.colors.border, colorScheme: 'dark' }}
        />
      </div>
      <span className={`text-xs ${index === 0 ? 'mt-5' : ''}`} style={{ color: theme.colors.textDisabled }}>a</span>
      <div className="flex-1">
        {index === 0 && (
          <label className="text-xs mb-1 block" style={{ color: theme.colors.textDisabled }}>Fin</label>
        )}
        <input
          type="time"
          value={brk.end}
          onChange={(e) => onChange(index, 'end', e.target.value)}
          className="w-full rounded-lg px-3 py-2 text-sm text-white border outline-none"
          style={{ backgroundColor: theme.colors.bgSecondary, borderColor: theme.colors.border, colorScheme: 'dark' }}
        />
      </div>
      {canRemove && (
        <button
          onClick={() => onRemove(index)}
          className="w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer hover:bg-red-500/10"
          style={{ color: theme.colors.textMuted }}
          title="Eliminar descanso"
        >
          <i className="ri-delete-bin-line text-sm text-red-400" />
        </button>
      )}
      {!canRemove && <div className="w-9" />}
    </div>
  );
}

// ─── Time Range Badge ─────────────────────────────────────────────────────────

function TimeRangeBadge({ start, end }: { start: string; end: string }) {
  const startIsNight = isNightTime(start);
  const endIsNight = isNightTime(end);

  return (
    <span className="flex items-center gap-1 text-sm font-mono" style={{ color: theme.colors.textSecondary }}>
      {startIsNight && (
        <span className="text-xs" title="Turno noche" style={{ color: '#a78bfa' }}>
          <i className="ri-moon-line" />
        </span>
      )}
      {start}
      <span style={{ color: theme.colors.textDisabled }}> – </span>
      {endIsNight && !startIsNight && (
        <span className="text-xs" title="Turno noche" style={{ color: '#a78bfa' }}>
          <i className="ri-moon-line" />
        </span>
      )}
      {end}
    </span>
  );
}

// ─── Day Row ──────────────────────────────────────────────────────────────────

interface DayRowProps {
  row: AvailabilityRow;
  onSave: (row: Omit<AvailabilityRow, 'id' | 'barber_id' | 'created_at' | 'updated_at'>) => Promise<void>;
}

function DayRow({ row, onSave }: DayRowProps) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Normalize breaks from row (fallback to legacy break_start/break_end)
  const normalizeBreaks = (): BreakInterval[] => {
    if (row.breaks && row.breaks.length > 0) return row.breaks;
    if (row.break_start && row.break_end) {
      return [{ start: fmt(row.break_start), end: fmt(row.break_end) }];
    }
    return [];
  };

  const [form, setForm] = useState({
    is_working: row.is_working,
    start_time: fmt(row.start_time),
    end_time: fmt(row.end_time),
    breaks: normalizeBreaks(),
  });

  const handleToggleWorking = async () => {
    setSaving(true);
    try {
      await onSave({
        day_of_week: row.day_of_week,
        is_working: !row.is_working,
        start_time: row.start_time,
        end_time: row.end_time,
        break_start: row.break_start,
        break_end: row.break_end,
        breaks: row.breaks ?? [],
      });
    } finally {
      setSaving(false);
    }
  };

  const handleBreakChange = (index: number, field: 'start' | 'end', value: string) => {
    setForm((f) => {
      const updated = [...f.breaks];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, breaks: updated };
    });
  };

  const handleAddBreak = () => {
    setForm((f) => ({
      ...f,
      breaks: [...f.breaks, { start: '15:00', end: '15:30' }],
    }));
  };

  const handleRemoveBreak = (index: number) => {
    setForm((f) => ({
      ...f,
      breaks: f.breaks.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Keep legacy fields in sync with first break for backwards compat
      const firstBreak = form.breaks[0] ?? null;
      await onSave({
        day_of_week: row.day_of_week,
        is_working: form.is_working,
        start_time: form.start_time || '08:30',
        end_time: form.end_time || '18:00',
        break_start: firstBreak ? firstBreak.start : null,
        break_end: firstBreak ? firstBreak.end : null,
        breaks: form.breaks,
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm({
      is_working: row.is_working,
      start_time: fmt(row.start_time),
      end_time: fmt(row.end_time),
      breaks: normalizeBreaks(),
    });
    setEditing(false);
  };

  // Determine if end_time is in night territory
  const endIsNight = form.end_time ? isNightTime(form.end_time) : false;
  const displayBreaks = row.breaks && row.breaks.length > 0
    ? row.breaks
    : (row.break_start && row.break_end ? [{ start: fmt(row.break_start), end: fmt(row.break_end) }] : []);

  return (
    <motion.div
      layout
      className="rounded-xl border overflow-hidden"
      style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.bgSurface }}
    >
      {/* Header row */}
      <div className="flex items-center gap-4 px-5 py-4">
        {/* Day label */}
        <div className="w-28 flex-shrink-0">
          <p className="text-white text-sm font-semibold">{DAY_LABELS_FULL[row.day_of_week]}</p>
          <p className="text-xs mt-0.5" style={{ color: theme.colors.textMuted }}>
            {DAY_LABELS[row.day_of_week]}
          </p>
        </div>

        {/* Toggle */}
        <button
          onClick={handleToggleWorking}
          disabled={saving}
          className="relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 disabled:opacity-50"
          style={{ backgroundColor: row.is_working ? theme.colors.primary : theme.colors.border }}
        >
          <span
            className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-black transition-transform"
            style={{ transform: row.is_working ? 'translateX(20px)' : 'translateX(0)' }}
          />
        </button>

        {/* Hours summary */}
        <div className="flex-1 flex items-center gap-2 flex-wrap">
          {row.is_working ? (
            <>
              <TimeRangeBadge start={fmt(row.start_time)} end={fmt(row.end_time)} />

              {/* Night badge if end >= 19:00 */}
              {isNightTime(fmt(row.end_time)) && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
                  style={{ backgroundColor: 'rgba(167,139,250,0.12)', color: '#a78bfa' }}
                >
                  <i className="ri-moon-line text-xs" />
                  Noche
                </span>
              )}

              {/* Break badges */}
              {displayBreaks.map((brk, i) => (
                <span
                  key={i}
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: theme.colors.primaryMuted, color: theme.colors.textMuted }}
                >
                  <i className="ri-rest-time-line mr-1" />
                  {brk.start}–{brk.end}
                </span>
              ))}
            </>
          ) : (
            <span className="text-sm" style={{ color: theme.colors.textDisabled }}>Día libre</span>
          )}
        </div>

        {/* Edit button */}
        {row.is_working && (
          <button
            onClick={() => setEditing((e) => !e)}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer hover:bg-white/8"
            style={{ color: theme.colors.textMuted }}
          >
            <i className={`ri-${editing ? 'close' : 'edit'}-line text-sm`} />
          </button>
        )}
      </div>

      {/* Edit panel */}
      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <div
              className="px-5 pb-5 pt-4 space-y-5"
              style={{ borderTop: `1px solid ${theme.colors.border}` }}
            >
              {/* ── Working hours ── */}
              <div>
                <p className="text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: theme.colors.textMuted }}>
                  <i className="ri-sun-line" />
                  Horas de Trabajo
                </p>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="text-xs mb-1 block" style={{ color: theme.colors.textDisabled }}>Inicio</label>
                    <input
                      type="time"
                      value={form.start_time}
                      onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-sm text-white border outline-none"
                      style={{ backgroundColor: theme.colors.bgSecondary, borderColor: theme.colors.border, colorScheme: 'dark' }}
                    />
                  </div>
                  <span className="text-xs mt-5" style={{ color: theme.colors.textDisabled }}>a</span>
                  <div className="flex-1">
                    <label className="text-xs mb-1 block" style={{ color: theme.colors.textDisabled }}>
                      Fin
                      {endIsNight && (
                        <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: 'rgba(167,139,250,0.15)', color: '#a78bfa' }}>
                          <i className="ri-moon-line" /> Noche
                        </span>
                      )}
                    </label>
                    <input
                      type="time"
                      value={form.end_time}
                      onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                      className="w-full rounded-lg px-3 py-2 text-sm text-white border outline-none"
                      style={{
                        backgroundColor: theme.colors.bgSecondary,
                        borderColor: endIsNight ? 'rgba(167,139,250,0.4)' : theme.colors.border,
                        colorScheme: 'dark',
                      }}
                    />
                  </div>
                </div>

                {/* Night indicator */}
                {endIsNight && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
                    style={{ backgroundColor: 'rgba(167,139,250,0.08)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.2)' }}
                  >
                    <i className="ri-moon-line" />
                    Turno nocturno activo — horario extendido hasta las {form.end_time}
                  </motion.div>
                )}
              </div>

              {/* ── Breaks ── */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold flex items-center gap-2" style={{ color: theme.colors.textMuted }}>
                    <i className="ri-rest-time-line" />
                    Descansos
                    <span className="font-normal" style={{ color: theme.colors.textDisabled }}>(opcional)</span>
                  </p>
                  <button
                    onClick={handleAddBreak}
                    className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg cursor-pointer whitespace-nowrap transition-colors hover:bg-white/8"
                    style={{ color: theme.colors.primary, border: `1px solid ${theme.colors.primary}30` }}
                  >
                    <i className="ri-add-line" />
                    Agregar descanso
                  </button>
                </div>

                {form.breaks.length === 0 ? (
                  <p className="text-xs py-2" style={{ color: theme.colors.textDisabled }}>
                    Sin descansos configurados
                  </p>
                ) : (
                  <div className="space-y-2">
                    {form.breaks.map((brk, i) => (
                      <BreakItem
                        key={i}
                        brk={brk}
                        index={i}
                        onChange={handleBreakChange}
                        onRemove={handleRemoveBreak}
                        canRemove={form.breaks.length > 0}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* ── Actions ── */}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap transition-colors hover:bg-white/5"
                  style={{ color: theme.colors.textMuted }}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors disabled:opacity-50"
                  style={{ backgroundColor: theme.colors.primary, color: '#000' }}
                >
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ─── Weekly Schedule ──────────────────────────────────────────────────────────

interface WeeklyScheduleProps {
  availability: AvailabilityRow[];
  onSave: (row: Omit<AvailabilityRow, 'id' | 'barber_id' | 'created_at' | 'updated_at'>) => Promise<void>;
}

export default function WeeklySchedule({ availability, onSave }: WeeklyScheduleProps) {
  const rows = Array.from({ length: 7 }, (_, i) => {
    const found = availability.find((a) => a.day_of_week === i);
    return found ?? DEFAULT_ROW(i);
  });

  const hasNightShift = rows.some((r) => r.is_working && timeToMinutes(fmt(r.end_time)) >= NIGHT_START);

  return (
    <div className="space-y-2">
      {/* Night shift legend */}
      {hasNightShift && (
        <div
          className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg mb-3"
          style={{ backgroundColor: 'rgba(167,139,250,0.08)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.15)' }}
        >
          <i className="ri-moon-line" />
          <span>Los días con <strong>turno noche</strong> tienen horario extendido a partir de las 19:00</span>
        </div>
      )}

      {rows.map((row) => (
        <DayRow key={row.day_of_week} row={row} onSave={onSave} />
      ))}
    </div>
  );
}
